import React from 'react';
import { getIncludedQuoteLineColumns, getQuoteLineColumnDisplayValue } from '../utils/quoteLineColumns';
import { buildQuoteTotalDisplayRowsFromSummary, formatQuoteTotalRowValue } from '../utils/quoteTotals';

type QuotePreviewHeaderField = {
  id: string;
  label: string;
  value: string;
};

type QuotePreviewHeaderBlock = {
  id: string;
  slotId?: string;
  label: string;
  slotRow?: number;
  slotCol?: number;
  textAlign?: 'left' | 'center' | 'right';
  fields: QuotePreviewHeaderField[];
};

type Props = {
  caseQuotePreview: any | null;
  caseQuoteTemplate: any | null;
  caseQuotePreviewHeaderBlocks: QuotePreviewHeaderBlock[];
  styles: any;
  onClose: () => void;
};

export const TechDashboardQuotePreviewModal = React.memo(function TechDashboardQuotePreviewModal({
  caseQuotePreview,
  caseQuoteTemplate,
  caseQuotePreviewHeaderBlocks,
  styles,
  onClose
}: Props) {
  if (!caseQuotePreview) return null;
  const quoteLineColumns = getIncludedQuoteLineColumns(
    caseQuotePreview?.lineColumns ||
      caseQuotePreview?.totals?.lineColumns ||
      caseQuoteTemplate?.lineColumns
  );
  const quoteTotalRows = buildQuoteTotalDisplayRowsFromSummary(
    caseQuotePreview?.totals,
    caseQuotePreview?.totalSettings || caseQuoteTemplate?.totalSettings
  );
  const requireSignature =
    typeof caseQuotePreview?.requireSignature === 'boolean'
      ? caseQuotePreview.requireSignature
      : (caseQuoteTemplate?.requireSignature ?? false);
  const signatureLabel =
    caseQuotePreview?.signatureLabel ||
    caseQuoteTemplate?.signatureLabel ||
    'Customer approval signature';
  const requireTechnicianSignature =
    typeof caseQuotePreview?.requireTechnicianSignature === 'boolean'
      ? caseQuotePreview.requireTechnicianSignature
      : (caseQuoteTemplate?.requireTechnicianSignature ?? false);
  const technicianSignatureLabel =
    caseQuotePreview?.technicianSignatureLabel ||
    caseQuoteTemplate?.technicianSignatureLabel ||
    'Technician signature';
  const approvalPlacementRaw =
    String(caseQuotePreview?.approvalPlacement || caseQuoteTemplate?.approvalPlacement || '').trim();
  const approvalPlacement =
    approvalPlacementRaw === 'footer_c1' ||
    approvalPlacementRaw === 'footer_c2' ||
    approvalPlacementRaw === 'footer_c3'
      ? approvalPlacementRaw
      : approvalPlacementRaw === 'footer'
        ? 'footer_c1'
      : 'footer_c3';
  const footerSlotOrder = ['footer_c1', 'footer_c2', 'footer_c3'] as const;
  const approvalFooterSlots = (() => {
    const slots: Array<'footer_c1' | 'footer_c2' | 'footer_c3'> = [];
    const addSlot = (value: any) => {
      const raw = String(value || '').trim();
      if ((raw === 'footer_c1' || raw === 'footer_c2' || raw === 'footer_c3') && !slots.includes(raw)) {
        slots.push(raw);
      }
    };
    const rawSlots =
      (caseQuotePreview as any)?.approvalFooterSlots ||
      (caseQuotePreview as any)?.totals?.approvalSettings?.approvalFooterSlots ||
      (caseQuoteTemplate as any)?.approvalFooterSlots;
    if (Array.isArray(rawSlots)) rawSlots.forEach(addSlot);
    if (slots.length === 0) addSlot(approvalPlacement);
    return slots.sort((a, b) => footerSlotOrder.indexOf(a) - footerSlotOrder.indexOf(b));
  })();
  const renderSignatureBlocks = () => {
    if (!requireSignature && !requireTechnicianSignature) return null;
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '0.7rem',
          marginTop: '0.85rem'
        }}
      >
        {requireSignature && (
          <div style={{ border: '1px solid #cbd5e1', borderRadius: '0.65rem', padding: '0.7rem 0.75rem' }}>
            <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>
              {signatureLabel}
            </div>
            <div style={{ fontSize: '0.95rem', lineHeight: 1.45 }}>
              <div>Name: {caseQuotePreview.customerApprovalName || '__________________'}</div>
              <div style={{ paddingTop: '1.35rem' }}>Signature: {caseQuotePreview.customerApprovalSignature || '__________________'}</div>
            </div>
          </div>
        )}
        {requireTechnicianSignature && (
          <div style={{ border: '1px solid #cbd5e1', borderRadius: '0.65rem', padding: '0.7rem 0.75rem' }}>
            <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>
              {technicianSignatureLabel}
            </div>
            <div style={{ fontSize: '0.95rem', lineHeight: 1.45 }}>
              <div>Name: __________________</div>
              <div style={{ paddingTop: '1.35rem' }}>Signature: __________________</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={{ ...styles.modalContentWide, maxWidth: '1220px' }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{caseQuotePreview.title}</h2>
          <button style={styles.closeButton} onClick={onClose}>
            X
          </button>
        </div>

        <div style={styles.infoSection}>
          <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>
            Print preview: {caseQuoteTemplate?.name || caseQuotePreview.templateName || 'Quote'}
          </div>
          <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '0.8rem' }}>
            Email / print quote layout
          </div>

          {caseQuotePreviewHeaderBlocks.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: '0.75rem',
                marginBottom: '0.9rem'
              }}
            >
                {caseQuotePreviewHeaderBlocks.map(block => (
                  <div
                    key={`preview-header-${block.slotId || block.id}`}
                    style={{
                      border: '1px solid #cbd5e1',
                      borderRadius: '0.65rem',
                    padding: '0.7rem 0.75rem',
                    gridRow: block.slotRow ? `${block.slotRow}` : undefined,
                    gridColumn: block.slotCol ? `${block.slotCol}` : undefined,
                    textAlign: block.textAlign || 'left'
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: '0.25rem', textAlign: block.textAlign || 'left' }}>
                    {block.label}
                  </div>
                  {block.fields.map(field => (
                    <div
                      key={`preview-field-${field.id}`}
                      style={{ fontSize: '0.92rem', lineHeight: 1.45, textAlign: block.textAlign || 'left' }}
                    >
                      <span style={{ color: '#64748b' }}>{field.label}: </span>
                      <span>{field.value}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {caseQuotePreview.description && (
            <div
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '0.65rem',
                padding: '0.75rem',
                marginBottom: '0.85rem',
                whiteSpace: 'pre-wrap'
              }}
            >
              {caseQuotePreview.description}
            </div>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {quoteLineColumns.map(column => (
                  <th
                    key={`quote-preview-head-${column.id}`}
                    style={{
                      textAlign: column.align,
                      padding: '0.45rem',
                      borderBottom: '1px solid #cbd5e1',
                      verticalAlign: 'top'
                    }}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(caseQuotePreview.lineItems || []).map((line: any, index: number) => (
                <tr key={`${line?.id || 'line'}-${index}`}>
                  {quoteLineColumns.map(column => (
                    <td
                      key={`${line?.id || 'line'}-${index}-${column.id}`}
                      style={{
                        padding: '0.45rem',
                        textAlign: column.align,
                        borderBottom: '1px solid #e5e7eb',
                        verticalAlign: 'top'
                      }}
                    >
                      {getQuoteLineColumnDisplayValue(line, column.id)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'grid', justifyContent: 'end', gap: '0.25rem', marginTop: '0.8rem' }}>
            {(quoteTotalRows.length > 0
              ? quoteTotalRows
              : [{ id: 'grandTotal', label: 'Total', value: Number(caseQuotePreview?.totals?.grandTotal || 0), emphasised: true }]
            ).map(row => (
              <div
                key={`case-quote-total-${row.id}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto minmax(90px, auto)',
                  gap: '1rem',
                  textAlign: 'right',
                  ...(row.emphasised
                    ? { borderTop: '1px solid #cbd5e1', paddingTop: '0.35rem', fontWeight: 800, fontSize: '1.05rem' }
                    : { fontSize: '0.95rem' })
                }}
              >
                <span>{row.label}</span>
                <strong>{formatQuoteTotalRowValue(row)}</strong>
              </div>
            ))}
          </div>

          {caseQuotePreview.customerDecision && (
            <div style={{ marginTop: '0.45rem', color: '#0f172a', fontWeight: 600 }}>
              {caseQuotePreview.customerDecision === 'ACCEPT'
                ? 'Customer accepted'
                : caseQuotePreview.customerDecision === 'DENY'
                ? 'Customer denied'
                : 'Customer requested adjustment'}
              {caseQuotePreview.decisionAt
                ? ` on ${new Date(caseQuotePreview.decisionAt).toLocaleString()}`
                : ''}
              {caseQuotePreview.decisionBy ? ` by ${caseQuotePreview.decisionBy}` : ''}
            </div>
          )}

          <div style={{ marginTop: '0.75rem', marginBottom: '0.85rem', color: '#64748b', fontSize: '0.9rem' }}>
            Customer responses are submitted from the customer portal.
          </div>

          {approvalFooterSlots.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: '0.75rem'
              }}
            >
              {[1, 2, 3].map(column => {
                const selectedColumns = approvalFooterSlots.map(slot => footerSlotOrder.indexOf(slot) + 1);
                const startColumn = Math.min(...selectedColumns);
                const endColumn = Math.max(...selectedColumns);
                const span = endColumn - startColumn + 1;
                if (column < startColumn || column > endColumn) {
                  return <div key={`case-quote-footer-${column}`} />;
                }
                if (column !== startColumn) return null;
                return (
                  <div key={`case-quote-footer-${column}`} style={{ gridColumn: `span ${span}` }}>
                    {renderSignatureBlocks()}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

